import { apiClient } from '@/lib/api-client';

export type OnboardingPayload = {
  user_id: string;
  occupation: string | null;
  building_type: string | null;
  use_case: string | null;
  heard_from: string | null;
};

export type OnboardingAnswers = Omit<OnboardingPayload, 'user_id'>;

export type ChecklistSteps = {
  extensionInstalled: boolean;
  sdkCopied: boolean;
  dashboardExplored: boolean;
};

export type OnboardingStatus = {
  answers: OnboardingAnswers;
  dismissed: boolean;
  completed: boolean;
  introComplete: boolean;
  checklistSteps: ChecklistSteps;
};

export type OnboardingTourState = {
  projectId: string;
  step: number;
};

const DEV = import.meta.env.VITE_DEV_AUTH === 'true';

const EMPTY_ANSWERS: OnboardingAnswers = {
  occupation: null,
  building_type: null,
  use_case: null,
  heard_from: null,
};

function draftKey(userId: string) {
  return `recost_onboarding_draft:${userId}`;
}

function statusKey(userId: string) {
  return `recost_onboarding_status:${userId}`;
}

function tourKey(userId: string) {
  return `recost_onboarding_tour:${userId}`;
}

function safeParse<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function normalizeAnswers(input: unknown): OnboardingAnswers {
  const value = (input ?? {}) as Partial<OnboardingAnswers>;
  return {
    occupation: typeof value.occupation === 'string' ? value.occupation : null,
    building_type: typeof value.building_type === 'string' ? value.building_type : null,
    use_case: typeof value.use_case === 'string' ? value.use_case : null,
    heard_from: typeof value.heard_from === 'string' ? value.heard_from : null,
  };
}

function mergeStatus(userId: string, partial: Partial<OnboardingStatus>) {
  const current = readLocalOnboardingStatus(userId);
  const next: OnboardingStatus = {
    ...current,
    ...partial,
    answers: partial.answers ? normalizeAnswers(partial.answers) : current.answers,
    checklistSteps: partial.checklistSteps
      ? { ...current.checklistSteps, ...partial.checklistSteps }
      : current.checklistSteps,
  };
  localStorage.setItem(statusKey(userId), JSON.stringify(next));
  return next;
}

export function readLocalOnboardingStatus(userId: string): OnboardingStatus {
  const saved = safeParse<OnboardingStatus>(localStorage.getItem(statusKey(userId)));
  const draft = safeParse<OnboardingAnswers>(sessionStorage.getItem(draftKey(userId)));

  return {
    answers: normalizeAnswers(draft ?? EMPTY_ANSWERS),
    dismissed: Boolean(saved?.dismissed),
    completed: Boolean(saved?.completed),
    introComplete: Boolean(saved?.introComplete),
    checklistSteps: {
      extensionInstalled: Boolean(saved?.checklistSteps?.extensionInstalled),
      sdkCopied: Boolean(saved?.checklistSteps?.sdkCopied),
      dashboardExplored: Boolean(saved?.checklistSteps?.dashboardExplored),
    },
  };
}

export function markChecklistStep(userId: string, step: keyof ChecklistSteps, value = true) {
  const current = readLocalOnboardingStatus(userId);
  const nextSteps = { ...current.checklistSteps, [step]: value };
  const allDone = nextSteps.extensionInstalled && nextSteps.sdkCopied && nextSteps.dashboardExplored;
  return mergeStatus(userId, {
    checklistSteps: nextSteps,
    ...(current.completed && !allDone ? { completed: false } : {}),
  });
}

export function saveLocalOnboardingDraft(userId: string, answers: Partial<OnboardingAnswers>) {
  const current = readLocalOnboardingStatus(userId).answers;
  const merged = { ...current };
  for (const key of Object.keys(answers) as (keyof OnboardingAnswers)[]) {
    const value = answers[key];
    merged[key] = typeof value === 'string' ? value : value ?? current[key];
  }
  sessionStorage.setItem(draftKey(userId), JSON.stringify(merged));
  mergeStatus(userId, { answers: merged });
  return merged;
}

export function markOnboardingDismissed(userId: string) {
  clearOnboardingTour(userId);
  sessionStorage.removeItem(draftKey(userId));
  localStorage.removeItem(draftKey(userId));
  return mergeStatus(userId, { dismissed: true });
}

export function markOnboardingIntroComplete(userId: string) {
  return mergeStatus(userId, { introComplete: true, dismissed: false });
}

export function resetOnboardingIntro(userId: string) {
  return mergeStatus(userId, { introComplete: false, dismissed: false });
}

export function markOnboardingCompleted(userId: string) {
  clearOnboardingTour(userId);
  sessionStorage.removeItem(draftKey(userId));
  localStorage.removeItem(draftKey(userId));
  return mergeStatus(userId, { completed: true, dismissed: false, introComplete: true });
}

export function getActiveOnboardingTour(userId: string): OnboardingTourState | null {
  return safeParse<OnboardingTourState>(sessionStorage.getItem(tourKey(userId)));
}

export function startOnboardingTour(userId: string, projectId: string) {
  const state: OnboardingTourState = { projectId, step: 0 };
  sessionStorage.setItem(tourKey(userId), JSON.stringify(state));
  // Reset completed so shouldShowProjectTour isn't blocked on reload.
  mergeStatus(userId, { completed: false, introComplete: true });
  return state;
}

export function updateOnboardingTourStep(userId: string, step: number) {
  const current = getActiveOnboardingTour(userId);
  if (!current) return null;
  const next = { ...current, step };
  sessionStorage.setItem(tourKey(userId), JSON.stringify(next));
  return next;
}

export function clearOnboardingTour(userId: string) {
  sessionStorage.removeItem(tourKey(userId));
}

export function resetOnboardingState(userId: string) {
  clearOnboardingTour(userId);
  sessionStorage.removeItem(draftKey(userId));
  localStorage.removeItem(draftKey(userId));
  localStorage.removeItem(statusKey(userId));
}

function getCompletedFromAnswers(answers: OnboardingAnswers) {
  return Object.values(answers).every((value) => value !== null);
}

function parseRemoteStatus(payload: unknown): Partial<OnboardingStatus> | null {
  const data = (payload ?? {}) as Record<string, unknown>;
  const nested = (data.data ?? data.status ?? data.onboarding ?? data) as Record<string, unknown>;
  const answers = normalizeAnswers(nested);
  const completed =
    typeof nested.completed === 'boolean'
      ? nested.completed
      : getCompletedFromAnswers(answers);

  if (!Object.values(answers).some(Boolean) && typeof nested.completed !== 'boolean') {
    return null;
  }

  return {
    answers,
    completed,
    introComplete: completed,
  };
}

export async function getOnboardingStatus(userId: string): Promise<OnboardingStatus> {
  const local = readLocalOnboardingStatus(userId);
  if (DEV) return local;

  for (const path of ['/onboarding/status', '/onboarding']) {
    try {
      const response = await apiClient.get<unknown>(path);
      const remote = parseRemoteStatus(response);
      if (!remote) continue;
      return mergeStatus(userId, remote);
    } catch {
      // Missing or unready endpoints should not block the frontend flow.
    }
  }

  return local;
}

export async function saveOnboarding(payload: OnboardingPayload): Promise<{ ok: boolean; usedLocalFallback: boolean }> {
  saveLocalOnboardingDraft(payload.user_id, payload);

  if (DEV) {
    return { ok: true, usedLocalFallback: true };
  }

  try {
    await apiClient.post('/onboarding', payload);
    return { ok: true, usedLocalFallback: false };
  } catch {
    return { ok: false, usedLocalFallback: true };
  }
}
